import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import { getFormatter } from '../../../../core/utils/get-formatter.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesIdToColorScale } from '../../../context/XYChartColorScales.context.js';
import type {
  BarMetadata,
  BarSeriesToMetadata,
  BarSeriesWithDatapoints,
  RectDatapointInternal,
  MapDatapoints,
} from '../../../types/xy-chart-internals.js';
import type { BarDatapoint } from '../../../types/xy-chart.js';
import { buildBarSeriesActionItem } from '../../../utils/build-series-action-item.js';
import { getColorFromScaleAndData } from '../../../utils/coloring/get-color-from-scale-and-data.js';
import {
  type HoveredDatapoint,
  type HoveredSeries,
  isBarTooltipContext,
  isRectTooltipContext,
  type HoveredSeriesToMetadata,
  type TooltipContextHoveredPoints,
} from '../../../utils/type-guards.js';

export interface BuildHoveredPointTooltipHeaderPropsArgs<
  T extends HoveredSeries,
  K extends HoveredDatapoint<T>,
> {
  datapoint: K;
  series: T;
  seriesToMetadata: HoveredSeriesToMetadata<T>;
  seriesWithDatapoints: BarSeriesWithDatapoints[];
  datapointMetadataMap: MapDatapoints;
  colorScaleMap: SeriesIdToColorScale;
}

export const buildHoveredPointTooltipHeaderProps = <
  T extends HoveredSeries,
  K extends (T extends BarSeriesWithDatapoints
    ? BarDatapoint
    : RectDatapointInternal),
>({
  datapoint,
  series,
  seriesToMetadata,
  seriesWithDatapoints,
  datapointMetadataMap,
  colorScaleMap,
}: BuildHoveredPointTooltipHeaderPropsArgs<T, K>) => {
  let name = '';
  let valueToDisplay = '';
  let colorToDisplay = '';
  let actions: (() => SeriesActionsTemplate) | undefined;

  const metadata = safeGet(seriesToMetadata, series);

  const enrichedDatapoint: TooltipContextHoveredPoints = {
    series,
    datapoint,
    metadata: metadata,
  };

  if (isRectTooltipContext(enrichedDatapoint)) {
    const colorScale = safeGet(colorScaleMap, series.id);
    const datapointMetadata = safeGet(datapointMetadataMap, datapoint);
    const rawValue = enrichedDatapoint.datapoint.value;
    const { valueAccessorLabel, valueAccessor, valueFormatter, valueUnit } =
      enrichedDatapoint.metadata;

    name = valueAccessorLabel ?? valueAccessor ?? '';

    valueToDisplay =
      typeof rawValue === 'number'
        ? getFormatter(valueFormatter)(rawValue, valueUnit)
        : (rawValue?.toString() ?? '');

    colorToDisplay =
      rawValue !== undefined
        ? (getColorFromScaleAndData(
            colorScale,
            enrichedDatapoint.datapoint.value,
            datapointMetadata.colorOverride,
          ) ?? '')
        : '';

    const metadataActions = enrichedDatapoint.metadata.actions;

    actions = metadataActions
      ? () => metadataActions(enrichedDatapoint.datapoint)
      : undefined;
  } else if (isBarTooltipContext(enrichedDatapoint)) {
    const {
      color,
      name: barName,
      nameSegments: barNameSegments,
    } = metadata as BarMetadata;
    const datapointMetadata = safeGet(datapointMetadataMap, datapoint);

    name = barName ?? '';
    colorToDisplay = datapointMetadata.colorOverride ?? color?.Default ?? '';

    const metadataActions = enrichedDatapoint.metadata.actions;
    actions = metadataActions
      ? () => {
          const actionItem = buildBarSeriesActionItem({
            datapoint: enrichedDatapoint.datapoint,
            allSeries: seriesWithDatapoints,
            seriesToMetadata:
              seriesToMetadata as unknown as BarSeriesToMetadata,
            datapointMetadataMap,
            currentSeries: enrichedDatapoint.series,
          });
          return metadataActions(actionItem);
        }
      : undefined;

    return {
      color: colorToDisplay,
      name,
      nameSegments: barNameSegments,
      value: valueToDisplay,
      actions,
    };
  }

  return {
    color: colorToDisplay,
    name,
    value: valueToDisplay,
    actions,
  };
};
